import { setDbManagerTabs } from '../../store/tabsStore';
import { useNotificationStore } from '../../store/notificationStore';
import { scriptForRun } from '../dbSqlStatements';
import { normalizeWhereFragment } from '../dbGridSql';
import { appendFilterCondition } from '../dbGridFilterClause';
import { liveLevels, cycleSort } from '../dbGridSort';
import CompactSelectPicker from '../ui/CompactSelectPicker';
import { engineOf, pageSizeOf, QUERY_MAX_ROWS_OPTIONS } from './dbResultsTabHelpers';

/** Filter, SQL run, sort and page-size actions behind the results toolbar. Plain function: it runs during render, after the early returns, and holds no hooks. */
export function dbToolbarActions({
  tab,
  result,
  queryMaxRows,
  runFreeQuery,
  runStartedAt,
  elapsedMs,
  filterDraft,
  setFilterDraft,
  sqlDraft,
  loading,
  runTableQuery,
  setSortState,
  sortRef,
  chunkSizeRef,
  sqlEditorApiRef,
  fetchRowCount,
  resetLoadMore,
  hasUnsavedGridChanges,
}) {
  /**
   * Apply a filter fragment built from a cell, AND-ing it onto whatever is
   * already committed.
   *
   * Draft and committed value move together here, exactly as commitFilter does:
   * the box has to show what actually ran, or the filter would have two
   * disagreeing sources of truth again (the bug A3-2 fixed). Guarded on
   * unsaved edits for the same reason the pager and sort are — a new WHERE
   * swaps out the row set that row-index-keyed pending edits address.
   */
  const applyFilterFragment = (fragment) => {
    if (!fragment || hasUnsavedGridChanges || loading) return;
    const next = appendFilterCondition(tab.filterText, fragment); // before any ORDER BY in the box
    setFilterDraft(next);
    setDbManagerTabs((prev) => prev.map((t) => (t.id === tab.id ? { ...t, filterText: next } : t)));
    resetLoadMore();
    runTableQuery(tab, next);
    fetchRowCount(tab, next);
  };

  const commitFilter = () => {
    // A changed WHERE clause swaps the addressable row set out from under
    // row-index-keyed pending edits — same invariant as the pager/sort.
    if (hasUnsavedGridChanges || loading) return;
    const cleaned = normalizeWhereFragment(filterDraft);
    if (cleaned !== filterDraft) setFilterDraft(cleaned);
    setDbManagerTabs((prev) => prev.map((t) => (t.id === tab.id ? { ...t, filterText: cleaned } : t)));
    // A new filter is a new result set — back to page 1, fresh count.
    resetLoadMore();
    runTableQuery(tab, filterDraft);
    fetchRowCount(tab, filterDraft);
  };

  // Ctrl+Enter: the selection, else the statement at the cursor — never the
  // whole script (owner, 30.09.2026; see components/dbSqlStatements.js).
  const commitSql = () => {
    const toRun = sqlEditorApiRef.current?.getRunnableSql?.() || '';
    if (!toRun.trim()) {
      useNotificationStore.getState().warning(
        'Put the cursor on a statement or select what to run. Cmd/Ctrl+Shift+Enter runs the whole script.',
        'No statement at the cursor'
      );
      return;
    }
    runFreeQuery(tab, toRun);
  };
  // Ctrl+Shift+Enter: the whole script, on purpose — split by the same rules.
  const commitScript = () => runFreeQuery(tab, scriptForRun(sqlDraft, { engine: engineOf(tab) }));

  /**
   * Set one column as THE sort, in a named direction.
   *
   * `handleSortColumn` below cycles asc -> desc -> off, which is right for a
   * header click but means reaching DESC takes two clicks and knowing how many
   * you need takes reading the current indicator first. The menu names both
   * directions instead, so picking one is a single, unambiguous act.
   *
   * Same re-query path as the cycle: this is a server-side ORDER BY, so DESC on
   * a large table returns the LAST rows the database holds, not the loaded page
   * reversed.
   */
  const handleOrderColumn = (col, dir) => {
    if (hasUnsavedGridChanges || loading) return;
    // Levels naming a column the current projection no longer has would make
    // the ORDER BY a query error, so drop them first — same guard the cycle uses.
    const base = liveLevels(sortRef.current, result?.columns);
    const alreadyExactly =
      base.length === 1 && base[0].col === col && base[0].dir === dir;
    // Picking the order that is already applied should not re-read the table.
    if (alreadyExactly) return;
    sortRef.current = [{ col, dir }];
    setSortState([{ col, dir }]);
    resetLoadMore();
    runTableQuery(tab, tab.filterText || '', { silent: true });
  };

  const handleClearSortColumn = (col) => {
    if (hasUnsavedGridChanges || loading) return;
    const next = liveLevels(sortRef.current, result?.columns).filter((l) => l.col !== col);
    sortRef.current = next;
    setSortState(next);
    resetLoadMore();
    runTableQuery(tab, tab.filterText || '', { silent: true });
  };

  const handleSortColumn = (col, additive = false) => {
    if (hasUnsavedGridChanges || loading) return;
    // Levels are dropped if the projection changed under them — an ORDER BY
    // naming a column the result no longer has is a query error.
    const base = liveLevels(sortRef.current, result?.columns);
    const next = cycleSort(base, col, { additive });
    sortRef.current = next;
    setSortState(next);
    resetLoadMore();
    runTableQuery(tab, tab.filterText || '', { silent: true });
  };

  // Formatted once here so the two grid instances cannot drift in how they
  // render the same number — or word the same Stop.
  const elapsedLabel =
    runStartedAt == null
      ? null
      : elapsedMs < 1000
        ? `${elapsedMs} ms`
        : `${(elapsedMs / 1000).toFixed(1)} s`;
  const stopTitle =
    engineOf(tab) === 'sqlite'
      ? 'Stop waiting. SQLite has no server-side cancel, so the statement still runs to completion.'
      : 'Cancel this query on the server';

  const pageSize = pageSizeOf(tab);

  const handleQueryMaxRowsChange = (n) => {
    if (!QUERY_MAX_ROWS_OPTIONS.includes(n) || loading) return;
    // Stored on the tab like pageSize, so it rides the workspace tab snapshot
    // and survives a tab switch. Deliberately does NOT re-run the query: the
    // cap applies to the next run, and silently re-executing arbitrary SQL
    // because somebody touched a dropdown is not something a database tool
    // should do.
    setDbManagerTabs((prev) => prev.map((t) => (t.id === tab.id ? { ...t, queryMaxRows: n } : t)));
  };

  // The cap for the NEXT run. One element: in the results footer, and in the
  // SQL bar before a query tab has any result (card 50122def: it could not be
  // set until a first run had returned rows).
  const maxRowsPicker = (
    <CompactSelectPicker
      className="dbm-page-size"
      value={queryMaxRows}
      onChange={handleQueryMaxRowsChange}
      options={QUERY_MAX_ROWS_OPTIONS.map((n) => ({ value: n, label: `${n.toLocaleString()} max` }))}
      disabled={loading}
      menuMinWidth={130}
      aria-label="Maximum rows to fetch"
    />
  );

  const handlePageSizeChange = (n) => {
    // No preset-membership check: any number the picker produced is already
    // sanitised and clamped, and rejecting a custom value here would make the
    // field accept input it then quietly discarded.
    if (!Number.isFinite(n) || n < 1 || hasUnsavedGridChanges || loading) return;
    // Stored on the tab itself so it rides the workspace tab snapshot (the
    // serializer persists pageSize) and survives tab switches.
    setDbManagerTabs((prev) => prev.map((t) => (t.id === tab.id ? { ...t, pageSize: n } : t)));
    chunkSizeRef.current = n;
    resetLoadMore();
    runTableQuery(tab, tab.filterText || '');
    fetchRowCount(tab, tab.filterText || '');
  };

  return {
    applyFilterFragment,
    commitFilter,
    commitSql,
    commitScript,
    handleOrderColumn,
    handleClearSortColumn,
    handleSortColumn,
    elapsedLabel,
    stopTitle,
    pageSize,
    maxRowsPicker,
    handlePageSizeChange,
  };
}
